import React, { useState } from "react";
import Plot from "react-plotly.js";
import "./AnalysisReport.css";

function AnalysisReport() {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeSection, setActiveSection] = useState("executive");

  const generateReport = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch("https://restaurant-analytics-backend.onrender.com/analyze", {
        method: "POST",
      });

      const data = await response.json();

      if (response.ok) {
        console.log("Enhanced analysis report received:", data);
        setReportData(data);
        setActiveSection("executive");
      } else {
        throw new Error(
          data.detail?.error ||
            data.detail ||
            "Failed to generate analysis report"
        );
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (typeof value === "number") {
      return `₹${value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return value;
  };

  const formatNumber = (value) => {
    if (typeof value === "number") {
      return value.toLocaleString();
    }
    return value;
  };

  const formatPercentage = (value) => {
    if (typeof value === "number") {
      return `${value.toFixed(1)}%`;
    }
    return value;
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case "critical":
        return "#dc2626";
      case "high":
        return "#ea580c";
      case "medium":
        return "#d97706";
      case "low":
        return "#65a30d";
      default:
        return "#6b7280";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "critical":
        return "#dc2626";
      case "high":
        return "#ea580c";
      case "medium":
        return "#d97706";
      case "low":
        return "#65a30d";
      default:
        return "#6b7280";
    }
  };

  const renderExecutiveSummary = () => {
    if (!reportData) return null;

    const { analysis_metadata, executive_summary, data_quality } = reportData;

    return (
      <div className="report-section">
        <div className="section-header">
          <h2>🎯 Executive Summary</h2>
          <p>Comprehensive overview of your business analytics</p>
        </div>

        {/* Analysis Overview Cards */}
        <div className="summary-grid">
          <div className="summary-card highlight">
            <div className="card-icon">📊</div>
            <div className="card-content">
              <h3>Analysis Status</h3>
              <p className="big-number">
                {executive_summary?.analysis_status || "Unknown"}
              </p>
              <small>Generated in {analysis_metadata?.execution_time}s</small>
            </div>
          </div>

          <div className="summary-card">
            <div className="card-icon">📦</div>
            <div className="card-content">
              <h3>Total Records</h3>
              <p className="big-number">
                {(() => {
                  const value =
                    analysis_metadata?.records_analyzed ||
                    executive_summary?.total_records ||
                    reportData?.analysis_metadata?.records_analyzed ||
                    0;
                  return formatNumber(parseInt(value) || 0);
                })()}
              </p>
              <small>Transactions processed</small>
            </div>
          </div>

          <div className="summary-card">
            <div className="card-icon">🏷️</div>
            <div className="card-content">
              <h3>Unique Products</h3>
              <p className="big-number">
                {(() => {
                  const value =
                    reportData?.product_performance?.product_summary
                      ?.total_products ||
                    reportData?.executive_summary?.unique_products ||
                    reportData?.product_analysis?.total_products ||
                    0;
                  return value === 0 ? "0" : formatNumber(parseInt(value) || 0);
                })()}
              </p>
              <small>Product varieties</small>
            </div>
          </div>

          <div className="summary-card">
            <div className="card-icon">🎯</div>
            <div className="card-content">
              <h3>Data Quality</h3>
              <p className="big-number quality-score">
                {(() => {
                  const score = data_quality?.overall_score || 0;
                  return formatPercentage(parseFloat(score) * 100);
                })()}
              </p>
              <small>Overall data quality</small>
            </div>
          </div>
        </div>

        {/* Data Quality Visualization */}
        {data_quality && (
          <div className="quality-section">
            <h3>📊 Data Quality Assessment</h3>
            <div className="quality-grid">
              <div className="quality-item">
                <div className="quality-info">
                  <span className="quality-label">Sales Data</span>
                  <span className="quality-score">
                    {formatPercentage(
                      (data_quality.sales_quality?.score || 0) * 100
                    )}
                  </span>
                </div>
                <div className="quality-bar">
                  <div
                    className="quality-fill sales"
                    style={{
                      width: `${
                        (data_quality.sales_quality?.score || 0) * 100
                      }%`,
                    }}
                  ></div>
                </div>
              </div>

              <div className="quality-item">
                <div className="quality-info">
                  <span className="quality-label">Cost Data</span>
                  <span className="quality-score">
                    {formatPercentage(
                      (data_quality.costs_quality?.score || 0) * 100
                    )}
                  </span>
                </div>
                <div className="quality-bar">
                  <div
                    className="quality-fill costs"
                    style={{
                      width: `${
                        (data_quality.costs_quality?.score || 0) * 100
                      }%`,
                    }}
                  ></div>
                </div>
              </div>

              {data_quality.raws_quality && (
                <div className="quality-item">
                  <div className="quality-info">
                    <span className="quality-label">Raw Materials</span>
                    <span className="quality-score">
                      {formatPercentage(
                        (data_quality.raws_quality?.score || 0) * 100
                      )}
                    </span>
                  </div>
                  <div className="quality-bar">
                    <div
                      className="quality-fill raws"
                      style={{
                        width: `${
                          (data_quality.raws_quality?.score || 0) * 100
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Performance Metrics */}
        {reportData.performance_metrics && (
          <div className="performance-section">
            <h3>⚡ Analysis Performance</h3>
            <div className="performance-grid">
              {Object.entries(reportData.performance_metrics).map(
                ([module, metrics]) => (
                  <div key={module} className="performance-item">
                    <div className="performance-info">
                      <span className="module-name">
                        {module.replace(/_/g, " ")}
                      </span>
                      <span className="execution-time">
                        {metrics.execution_time}s
                      </span>
                    </div>
                    <span className={`status ${metrics.status}`}>
                      {metrics.status}
                    </span>
                  </div>
                )
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderProfitAnalysis = () => {
    const profitData = reportData?.profit_analysis;
    if (!profitData || profitData.error) {
      return (
        <div className="report-section">
          <div className="section-header">
            <h2>💰 Profit Analysis</h2>
            <p>Financial performance and profitability insights</p>
          </div>
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <p>
              Profit analysis not available: {profitData?.error || "No data"}
            </p>
          </div>
        </div>
      );
    }

    const summary = profitData.summary || {};

    return (
      <div className="report-section">
        <div className="section-header">
          <h2>💰 Profit Analysis</h2>
          <p>Comprehensive financial performance analysis</p>
        </div>

        {/* Key Metrics Cards */}
        <div className="metrics-grid">
          <div className="metric-card revenue">
            <div className="metric-icon">💵</div>
            <div className="metric-content">
              <h3>Total Revenue</h3>
              <p className="metric-value">
                {formatCurrency(summary.total_revenue || 0)}
              </p>
              <div className="metric-trend">📈 Primary income</div>
            </div>
          </div>

          <div className="metric-card profit">
            <div className="metric-icon">💰</div>
            <div className="metric-content">
              <h3>Total Profit</h3>
              <p className="metric-value">
                {formatCurrency(summary.total_profit || 0)}
              </p>
              <div className="metric-trend">💎 Net earnings</div>
            </div>
          </div>

          <div className="metric-card margin">
            <div className="metric-icon">📊</div>
            <div className="metric-content">
              <h3>Profit Margin</h3>
              <p className="metric-value">
                {formatPercentage(summary.profit_margin || 0)}
              </p>
              <div className="metric-trend">🎯 Efficiency ratio</div>
            </div>
          </div>

          <div className="metric-card orders">
            <div className="metric-icon">🛍️</div>
            <div className="metric-content">
              <h3>Total Orders</h3>
              <p className="metric-value">
                {formatNumber(summary.total_orders || 0)}
              </p>
              <div className="metric-trend">📦 Transaction count</div>
            </div>
          </div>
        </div>

        {/* Enhanced Metrics */}
        {profitData.enhanced_metrics && (
          <div className="enhanced-metrics">
            <h3>📈 Enhanced Financial Metrics</h3>
            <div className="enhanced-grid">
              <div className="enhanced-item">
                <div className="enhanced-icon">💰</div>
                <div className="enhanced-content">
                  <span className="label">Profit per Order</span>
                  <span className="value">
                    {formatCurrency(
                      profitData.enhanced_metrics.profit_per_order || 0
                    )}
                  </span>
                </div>
              </div>
              <div className="enhanced-item">
                <div className="enhanced-icon">💵</div>
                <div className="enhanced-content">
                  <span className="label">Revenue per Order</span>
                  <span className="value">
                    {formatCurrency(
                      profitData.enhanced_metrics.revenue_per_order || 0
                    )}
                  </span>
                </div>
              </div>
              <div className="enhanced-item">
                <div className="enhanced-icon">📊</div>
                <div className="enhanced-content">
                  <span className="label">Cost Ratio</span>
                  <span className="value">
                    {formatPercentage(
                      (profitData.enhanced_metrics.cost_ratio || 0) * 100
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Top Products Table */}
        {profitData.product_profitability &&
          profitData.product_profitability.length > 0 && (
            <div className="top-products">
              <h3>🏆 Top Performing Products</h3>
              <div className="products-table-container">
                <table className="products-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Product Name</th>
                      <th>Revenue</th>
                      <th>Profit</th>
                      <th>Margin</th>
                      <th>Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profitData.product_profitability
                      .slice(0, 10)
                      .map((product, index) => (
                        <tr
                          key={index}
                          className={index < 3 ? "top-performer" : ""}
                        >
                          <td className="rank-cell">
                            <span
                              className={`rank-badge ${index < 3 ? "top" : ""}`}
                            >
                              {index + 1}
                            </span>
                          </td>
                          <td className="product-name">
                            {product.product_name || "Unknown"}
                          </td>
                          <td className="revenue-cell">
                            {formatCurrency(product.revenue || 0)}
                          </td>
                          <td className="profit-cell">
                            {formatCurrency(product.profit || 0)}
                          </td>
                          <td
                            className={`margin-cell ${
                              product.profit_margin > 30
                                ? "high"
                                : product.profit_margin > 15
                                ? "medium"
                                : "low"
                            }`}
                          >
                            {formatPercentage(product.profit_margin || 0)}
                          </td>
                          <td className="quantity-cell">
                            {formatNumber(product.quantity || 0)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        {/* Profit Distribution Chart */}
        {profitData.product_profitability &&
          profitData.product_profitability.length > 0 && (
            <div className="chart-section">
              <h3>📊 Profit Distribution Analysis</h3>
              <div className="chart-container">
                <Plot
                  data={[
                    {
                      x: profitData.product_profitability
                        .slice(0, 10)
                        .map((p) => p.product_name),
                      y: profitData.product_profitability
                        .slice(0, 10)
                        .map((p) => p.profit),
                      type: "bar",
                      marker: {
                        color: profitData.product_profitability
                          .slice(0, 10)
                          .map((p, i) =>
                            i < 3 ? "#10b981" : i < 6 ? "#3b82f6" : "#6b7280"
                          ),
                        line: { color: "#ffffff", width: 2 },
                      },
                      name: "Profit",
                      text: profitData.product_profitability
                        .slice(0, 10)
                        .map((p) => `₹${p.profit?.toFixed(0) || 0}`),
                      textposition: "outside",
                      hovertemplate:
                        "<b>%{x}</b><br>Profit: ₹%{y:,.2f}<extra></extra>",
                    },
                  ]}
                  layout={{
                    title: {
                      text: "Top 10 Products by Profit",
                      font: { size: 18, color: "#1f2937" },
                    },
                    xaxis: {
                      title: "Products",
                      tickangle: -45,
                      tickfont: { size: 12 },
                    },
                    yaxis: {
                      title: "Profit (₹)",
                      tickformat: ",.0f",
                    },
                    plot_bgcolor: "rgba(0,0,0,0)",
                    paper_bgcolor: "rgba(0,0,0,0)",
                    margin: { l: 60, r: 30, t: 60, b: 100 },
                    height: 400,
                    showlegend: false,
                  }}
                  config={{
                    displayModeBar: false,
                    responsive: true,
                  }}
                  useResizeHandler={true}
                  style={{ width: "100%", height: "400px" }}
                />
              </div>
            </div>
          )}
      </div>
    );
  };

  const renderProductPerformance = () => {
    const productData = reportData?.product_performance;
    if (!productData || productData.error) {
      return (
        <div className="report-section">
          <div className="section-header">
            <h2>🛍️ Product Performance</h2>
            <p>Comprehensive product analysis and insights</p>
          </div>
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <p>
              Product analysis not available: {productData?.error || "No data"}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="report-section">
        <div className="section-header">
          <h2>🛍️ Product Performance</h2>
          <p>Strategic product portfolio analysis</p>
        </div>

        {/* Product Categories Grid */}
        <div className="categories-grid">
          {productData.stars && productData.stars.length > 0 && (
            <div className="category-card stars">
              <div className="category-header">
                <div className="category-icon">⭐</div>
                <h3>Star Products</h3>
                <div className="category-count">{productData.stars.length}</div>
              </div>
              <div className="category-description">
                High margin, high volume winners
              </div>
              <ul className="category-list">
                {productData.stars.slice(0, 5).map((product, index) => (
                  <li key={index} className="category-item">
                    <span className="item-bullet">•</span>
                    {product}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {productData.high_margin_items &&
            productData.high_margin_items.length > 0 && (
              <div className="category-card hidden-gems">
                <div className="category-header">
                  <div className="category-icon">💎</div>
                  <h3>Hidden Gems</h3>
                  <div className="category-count">
                    {productData.high_margin_items.length}
                  </div>
                </div>
                <div className="category-description">
                  High margin, low volume opportunities
                </div>
                <ul className="category-list">
                  {productData.high_margin_items
                    .slice(0, 5)
                    .map((product, index) => (
                      <li key={index} className="category-item">
                        <span className="item-bullet">•</span>
                        {product}
                      </li>
                    ))}
                </ul>
              </div>
            )}

          {productData.low_margin_bestsellers &&
            productData.low_margin_bestsellers.length > 0 && (
              <div className="category-card leakage">
                <div className="category-header">
                  <div className="category-icon">⚠️</div>
                  <h3>Revenue Leakage</h3>
                  <div className="category-count">
                    {productData.low_margin_bestsellers.length}
                  </div>
                </div>
                <div className="category-description">
                  High volume, low margin items
                </div>
                <ul className="category-list">
                  {productData.low_margin_bestsellers
                    .slice(0, 5)
                    .map((product, index) => (
                      <li key={index} className="category-item">
                        <span className="item-bullet">•</span>
                        {product}
                      </li>
                    ))}
                </ul>
              </div>
            )}

          {productData.dead_items && productData.dead_items.length > 0 && (
            <div className="category-card dead-items">
              <div className="category-header">
                <div className="category-icon">💀</div>
                <h3>Dead Items</h3>
                <div className="category-count">
                  {productData.dead_items.length}
                </div>
              </div>
              <div className="category-description">
                Low margin, low volume underperformers
              </div>
              <ul className="category-list">
                {productData.dead_items.slice(0, 5).map((product, index) => (
                  <li key={index} className="category-item">
                    <span className="item-bullet">•</span>
                    {product}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Performance Leaders */}
        {productData.performance_metrics && (
          <div className="performance-leaders">
            <h3>🏆 Performance Leaders</h3>
            <div className="leaders-grid">
              <div className="leader-category">
                <div className="leader-header">
                  <div className="leader-icon">💰</div>
                  <h4>Revenue Leaders</h4>
                </div>
                <ol className="leader-list">
                  {productData.performance_metrics.revenue_leaders
                    ?.slice(0, 5)
                    .map((product, index) => (
                      <li key={index} className="leader-item">
                        <span className="leader-rank">{index + 1}</span>
                        {product}
                      </li>
                    ))}
                </ol>
              </div>

              <div className="leader-category">
                <div className="leader-header">
                  <div className="leader-icon">💎</div>
                  <h4>Profit Leaders</h4>
                </div>
                <ol className="leader-list">
                  {productData.performance_metrics.profit_leaders
                    ?.slice(0, 5)
                    .map((product, index) => (
                      <li key={index} className="leader-item">
                        <span className="leader-rank">{index + 1}</span>
                        {product}
                      </li>
                    ))}
                </ol>
              </div>

              <div className="leader-category">
                <div className="leader-header">
                  <div className="leader-icon">📊</div>
                  <h4>Margin Leaders</h4>
                </div>
                <ol className="leader-list">
                  {productData.performance_metrics.margin_leaders
                    ?.slice(0, 5)
                    .map((product, index) => (
                      <li key={index} className="leader-item">
                        <span className="leader-rank">{index + 1}</span>
                        {product}
                      </li>
                    ))}
                </ol>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderInsights = () => {
    // Get insights from multiple sources
    const synthesizedInsights = reportData?.synthesized_insights || {};
    const aiInsights = reportData?.ai_insights;
    const comprehensiveReport = reportData?.comprehensive_report;

    // Collect all insights
    const allInsights = [];

    // From synthesized insights
    if (synthesizedInsights.key_insights) {
      allInsights.push(...synthesizedInsights.key_insights);
    }

    // From AI insights
    if (aiInsights?.ai_generated_insights) {
      allInsights.push(...aiInsights.ai_generated_insights);
    }

    // From comprehensive report
    if (comprehensiveReport?.insights) {
      allInsights.push(...comprehensiveReport.insights);
    }

    // From individual modules
    [
      "profit_analysis",
      "product_performance",
      "operational_efficiency",
    ].forEach((module) => {
      const moduleData = reportData?.[module];
      if (moduleData?.insights) {
        allInsights.push(...moduleData.insights);
      }
    });

    return (
      <div className="report-section">
        <div className="section-header">
          <h2>💡 AI-Powered Insights</h2>
          <p>Machine learning generated business intelligence</p>
        </div>

        {allInsights.length > 0 ? (
          <div className="insights-container">
            <div className="insights-stats">
              <div className="insight-stat">
                <span className="stat-number">{allInsights.length}</span>
                <span className="stat-label">Total Insights</span>
              </div>
              <div className="insight-stat">
                <span className="stat-number">
                  {synthesizedInsights.insight_quality_score
                    ? formatPercentage(
                        synthesizedInsights.insight_quality_score * 100
                      )
                    : "N/A"}
                </span>
                <span className="stat-label">Quality Score</span>
              </div>
            </div>

            <div className="insights-list">
              {allInsights.slice(0, 15).map((insight, index) => (
                <div
                  key={index}
                  className={`insight-item ${index < 5 ? "priority" : ""}`}
                >
                  <div className="insight-icon">{index < 5 ? "🔥" : "💡"}</div>
                  <div className="insight-content">
                    <p>{insight}</p>
                    {index < 5 && (
                      <span className="priority-badge">High Priority</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="no-data">
            <div className="no-data-icon">🤖</div>
            <h3>Generating Insights...</h3>
            <p>
              AI insights will appear here after running a complete analysis
              with sufficient data.
            </p>
          </div>
        )}

        {/* Business Intelligence */}
        {synthesizedInsights?.business_intelligence &&
          synthesizedInsights.business_intelligence.length > 0 && (
            <div className="business-intelligence">
              <h3>🧠 Strategic Business Intelligence</h3>
              <div className="bi-insights">
                {synthesizedInsights.business_intelligence.map(
                  (insight, index) => (
                    <div key={index} className="bi-item">
                      <div className="bi-icon">🧠</div>
                      <div className="bi-content">
                        <p>{insight}</p>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

        {/* Executive AI Summary */}
        {aiInsights?.executive_summary && (
          <div className="executive-ai-summary">
            <h3>📋 Executive AI Summary</h3>
            <div className="ai-summary-grid">
              {Object.entries(aiInsights.executive_summary).map(
                ([key, value], index) => (
                  <div key={index} className="summary-item">
                    <div className="summary-label">
                      {key.replace(/_/g, " ").toUpperCase()}
                    </div>
                    <div className="summary-value">
                      {typeof value === "object"
                        ? JSON.stringify(value, null, 2)
                        : value}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderRecommendations = () => {
    // Get recommendations from multiple sources
    const advancedRecommendations = reportData?.advanced_recommendations || [];
    const legacyRecommendations = reportData?.recommendations || [];
    const comprehensiveReport = reportData?.comprehensive_report;

    // Collect all recommendations
    let allRecommendations = [...advancedRecommendations];

    // Add legacy recommendations if not already included
    legacyRecommendations.forEach((rec) => {
      if (
        typeof rec === "string" &&
        !allRecommendations.find((r) => r.description === rec)
      ) {
        allRecommendations.push({
          priority: "medium",
          category: "general",
          description: rec,
          action: "Review and implement",
          impact: "Business improvement",
        });
      }
    });

    // Add from comprehensive report
    if (comprehensiveReport?.recommendations) {
      comprehensiveReport.recommendations.forEach((rec) => {
        if (
          typeof rec === "string" &&
          !allRecommendations.find((r) => r.description === rec)
        ) {
          allRecommendations.push({
            priority: "medium",
            category: "comprehensive",
            description: rec,
            action: "Review and implement",
            impact: "Business improvement",
          });
        }
      });
    }

    return (
      <div className="report-section">
        <div className="section-header">
          <h2>📈 Strategic Recommendations</h2>
          <p>AI-generated actionable business recommendations</p>
        </div>

        {allRecommendations.length > 0 ? (
          <>
            <div className="recommendations-stats">
              <div className="rec-stat">
                <span className="stat-number">{allRecommendations.length}</span>
                <span className="stat-label">Total Recommendations</span>
              </div>
              <div className="rec-stat">
                <span className="stat-number">
                  {
                    allRecommendations.filter(
                      (r) => r.priority === "critical" || r.priority === "high"
                    ).length
                  }
                </span>
                <span className="stat-label">High Priority</span>
              </div>
            </div>

            <div className="recommendations-container">
              {allRecommendations.map((rec, index) => (
                <div
                  key={index}
                  className={`recommendation-card ${rec.priority || "medium"}`}
                >
                  <div className="rec-header">
                    <div className="rec-priority-badge">
                      <span
                        className="rec-priority"
                        style={{
                          backgroundColor: getPriorityColor(rec.priority),
                        }}
                      >
                        {rec.priority?.toUpperCase() || "MEDIUM"}
                      </span>
                      <span className="rec-category">
                        {rec.category?.replace(/_/g, " ") || "General"}
                      </span>
                    </div>
                    <div className="rec-index">#{index + 1}</div>
                  </div>

                  <div className="rec-content">
                    <h4 className="rec-title">{rec.description || rec}</h4>

                    {rec.action && (
                      <div className="rec-action">
                        <div className="rec-label">🎯 Action Required:</div>
                        <div className="rec-text">{rec.action}</div>
                      </div>
                    )}

                    {rec.impact && (
                      <div className="rec-impact">
                        <div className="rec-label">📊 Expected Impact:</div>
                        <div className="rec-text">{rec.impact}</div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="no-data">
            <div className="no-data-icon">📈</div>
            <h3>Generating Recommendations...</h3>
            <p>
              Strategic recommendations will appear here after running a
              complete analysis.
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderRedFlags = () => {
    const redFlags = reportData?.red_flags || [];

    return (
      <div className="report-section">
        <div className="section-header">
          <h2>🚩 Red Flags & Alerts</h2>
          <p>Critical issues requiring immediate attention</p>
        </div>

        {redFlags.length > 0 ? (
          <>
            <div className="red-flags-stats">
              <div className="flag-stat critical">
                <span className="stat-number">
                  {redFlags.filter((f) => f.severity === "critical").length}
                </span>
                <span className="stat-label">Critical</span>
              </div>
              <div className="flag-stat high">
                <span className="stat-number">
                  {redFlags.filter((f) => f.severity === "high").length}
                </span>
                <span className="stat-label">High</span>
              </div>
              <div className="flag-stat medium">
                <span className="stat-number">
                  {redFlags.filter((f) => f.severity === "medium").length}
                </span>
                <span className="stat-label">Medium</span>
              </div>
            </div>

            <div className="red-flags-container">
              {redFlags.map((flag, index) => (
                <div
                  key={index}
                  className={`red-flag-card ${flag.severity || "medium"}`}
                >
                  <div className="flag-header">
                    <div className="flag-severity-badge">
                      <span
                        className="flag-severity"
                        style={{
                          backgroundColor: getSeverityColor(flag.severity),
                        }}
                      >
                        {flag.severity?.toUpperCase() || "MEDIUM"}
                      </span>
                      <span className="flag-category">
                        {flag.category?.replace(/_/g, " ") || "General"}
                      </span>
                    </div>
                    <div className="flag-icon">🚩</div>
                  </div>

                  <div className="flag-content">
                    <h4 className="flag-title">
                      {flag.flag || flag.description || flag}
                    </h4>

                    {flag.description && flag.flag !== flag.description && (
                      <div className="flag-description">
                        <div className="flag-label">📝 Details:</div>
                        <div className="flag-text">{flag.description}</div>
                      </div>
                    )}

                    {flag.recommendation && (
                      <div className="flag-recommendation">
                        <div className="flag-label">💡 Recommended Action:</div>
                        <div className="flag-text">{flag.recommendation}</div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="no-flags">
            <div className="success-state">
              <div className="success-icon">✅</div>
              <h3>All Systems Green!</h3>
              <p>No critical issues detected in your business operations.</p>
              <div className="success-details">
                <div className="success-metric">
                  <span className="metric-icon">📊</span>
                  <span>Data Quality: Excellent</span>
                </div>
                <div className="success-metric">
                  <span className="metric-icon">💰</span>
                  <span>Financial Health: Good</span>
                </div>
                <div className="success-metric">
                  <span className="metric-icon">⚙️</span>
                  <span>Operations: Efficient</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const sectionTabs = [
    {
      id: "executive",
      label: "🎯 Executive Summary",
      render: renderExecutiveSummary,
    },
    { id: "profit", label: "💰 Profit Analysis", render: renderProfitAnalysis },
    {
      id: "products",
      label: "🛍️ Product Performance",
      render: renderProductPerformance,
    },
    { id: "insights", label: "💡 AI Insights", render: renderInsights },
    {
      id: "recommendations",
      label: "📈 Recommendations",
      render: renderRecommendations,
    },
    { id: "red_flags", label: "🚩 Red Flags", render: renderRedFlags },
  ];

  return (
    <div className="analysis-report">
      <div className="report-header">
        <div className="header-content">
          <div className="header-text">
            <h1>🧠 Enhanced Analytics Brain</h1>
            <p>Comprehensive AI-powered business intelligence and insights</p>
          </div>

          <div className="header-actions">
            <button
              onClick={generateReport}
              disabled={loading}
              className="generate-btn"
            >
              {loading ? (
                <>
                  <span className="btn-spinner"></span>
                  🔄 Analyzing...
                </>
              ) : (
                <>🚀 Generate Enhanced Analysis</>
              )}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <div className="error-content">
            <div className="error-icon">❌</div>
            <div className="error-text">
              <h3>Analysis Failed</h3>
              <p>{error}</p>
              <div className="error-suggestions">
                <p>
                  <strong>Troubleshooting Steps:</strong>
                </p>
                <ul>
                  <li>Ensure both sales and product cost data are uploaded</li>
                  <li>Check data quality and format</li>
                  <li>Try refreshing and generating the report again</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="loading-container">
          <div className="loading-animation">
            <div className="loading-spinner"></div>
            <div className="loading-pulse"></div>
          </div>
          <h2>🧠 AI Brain Processing...</h2>
          <p>
            Running comprehensive analysis through multiple intelligence engines
          </p>
          <div className="loading-steps">
            <div className="step active">📊 Assessing data quality</div>
            <div className="step active">🔄 Merging datasets intelligently</div>
            <div className="step active">💰 Analyzing profit patterns</div>
            <div className="step active">🛍️ Evaluating product performance</div>
            <div className="step active">🤖 Generating AI insights</div>
            <div className="step active">📈 Creating recommendations</div>
            <div className="step active">🚩 Detecting red flags</div>
          </div>
        </div>
      )}

      {reportData && (
        <div className="report-content">
          <div className="section-tabs">
            {sectionTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`tab-btn ${
                  activeSection === tab.id ? "active" : ""
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="section-content">
            {sectionTabs.find((tab) => tab.id === activeSection)?.render()}
          </div>
        </div>
      )}

      {reportData && (
        <div className="report-footer">
          <div className="footer-content">
            <div className="analysis-completion">
              <div className="completion-icon">🎉</div>
              <div className="completion-text">
                <h3>Analysis Complete</h3>
                <p>
                  Your business data has been processed through{" "}
                  {reportData.analysis_metadata?.analytics_modules || 0}{" "}
                  AI-powered analytics engines.
                </p>
                <p className="ai-ready">
                  🤖 <strong>AI Assistant Ready:</strong> You can now ask
                  detailed questions about your business performance!
                </p>
              </div>
            </div>

            <div className="next-steps">
              <h4>🚀 Recommended Next Steps</h4>
              <div className="steps-grid">
                <div className="step-item">
                  <div className="step-icon">🚩</div>
                  <div className="step-text">
                    Review red flags and take immediate action on critical
                    issues
                  </div>
                </div>
                <div className="step-item">
                  <div className="step-icon">📈</div>
                  <div className="step-text">
                    Implement high-priority recommendations
                  </div>
                </div>
                <div className="step-item">
                  <div className="step-icon">🤖</div>
                  <div className="step-text">
                    Use the AI Assistant to explore specific questions
                  </div>
                </div>
                <div className="step-item">
                  <div className="step-icon">📊</div>
                  <div className="step-text">
                    Monitor your progress with regular analysis updates
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnalysisReport;
